import { useEffect, useState, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import DollorIcon from "../../../assets/images/icons/dollar-sign.svg";
import MonthDropdown from "./MonthDropdown";
import { calculateHighestCategoryROI } from "../../../helper/dataFormat";
import * as actions from "../../../redux/actions/artistManager";
import { getUserPurchases } from "../../../redux/actions/billing";
import StarIcon from "../../../assets/images/icons/start-icon.svg";

export const ROICalculator = ({
    dspEstimatesEnabled = true,
    needsLinkArtist = false,
    onLinkArtist,
    onSubscribe,
}) => {
    const dispatch = useDispatch();
    const [isLoading, setIsLoading] = useState(false);
    const [month, setMonth] = useState('');
    const [year, setYear] = useState('');

    const { hasActiveSubscription, purchaseByMonthForROI } = useSelector((state) => state.billing);
    const activeArtistProfileId = useSelector(
        (state) => state.auth.access?.artist?.activeArtistProfileId || null
    );
    const artistManagerReducer = useSelector((state) => state.artistManager);

    const totalSubscriptionAmount = useMemo(() =>
        hasActiveSubscription ? (purchaseByMonthForROI?.statistics?.totalAmount || 0) : 0,
        [hasActiveSubscription, purchaseByMonthForROI?.statistics?.totalAmount]
    );

    const revenuesData = useMemo(() =>
        artistManagerReducer?.loggedRevenuesForROI?.data || {},
        [artistManagerReducer?.loggedRevenuesForROI?.data]
    );

    const expensesData = useMemo(() =>
        artistManagerReducer?.loggedExpensesForROI?.data || {},
        [artistManagerReducer?.loggedExpensesForROI?.data]
    );

    const totalRevenues = useMemo(() =>
        revenuesData?.totalAmount || 0,
        [revenuesData?.totalAmount]
    );

    const totalExpenses = useMemo(() =>
        (expensesData?.totalAmount || 0) + totalSubscriptionAmount,
        [expensesData?.totalAmount, totalSubscriptionAmount]
    );

    const spotifyStreamsData = useMemo(() =>
        artistManagerReducer?.spotifyDataForROI || {},
        [artistManagerReducer?.spotifyDataForROI]
    );

    const youtubeViewsData = useMemo(() =>
        artistManagerReducer?.youtubeDataForROI || {},
        [artistManagerReducer?.youtubeDataForROI]
    );

    const calculations = useMemo(() => {
        const streamsPlusViews = (spotifyStreamsData?.streamsDelta || 0) + (youtubeViewsData?.streamsDelta || 0);
        const totalPublishingEstimate = (streamsPlusViews * 0.0004) + (streamsPlusViews * 0.00091);
        const totalStreamingEstimate = (spotifyStreamsData?.streamsDelta * 0.0038) + (youtubeViewsData?.streamsDelta * 0.0006);
        const totalRevenuesWithEstimates = totalRevenues + totalStreamingEstimate + totalPublishingEstimate;
        const revenueForRoi = dspEstimatesEnabled ? totalRevenuesWithEstimates : totalRevenues;
        const roi = totalExpenses === 0 ? 0 : ((revenueForRoi - totalExpenses) / totalExpenses) * 100;

        return {
            totalPublishingEstimate,
            totalStreamingEstimate,
            totalRevenuesWithEstimates,
            revenueForRoi,
            roi
        };
    }, [spotifyStreamsData, youtubeViewsData, totalRevenues, totalExpenses, dspEstimatesEnabled]);

    const categoryROI = useMemo(() =>
        calculateHighestCategoryROI(
            revenuesData?.revenues || [],
            expensesData?.expenses || []
        ),
        [revenuesData?.revenues, expensesData?.expenses]
    );

    const handleMonthChange = useCallback((newMonth) => {
        setMonth(newMonth);
    }, []);

    const handleYearChange = useCallback((newYear) => {
        setYear(newYear);
    }, []);

    useEffect(() => {
        if (!month || !year) return;

        setIsLoading(true);

        const fetches = [
            dispatch(actions.fetchLogRevenues(toast, month, year, setIsLoading, "roi")),
            dispatch(actions.fetchLogExpenses(toast, month, year, setIsLoading, "roi")),
            dispatch(getUserPurchases({ month, year }, "roi")),
        ];

        if (dspEstimatesEnabled) {
            fetches.push(
                dispatch(actions.getStreamsData(toast, { source: "spotify", month, year }, setIsLoading, "roi")),
                dispatch(actions.getStreamsData(toast, { source: "youtube", month, year }, setIsLoading, "roi"))
            );
        }

        Promise.all(fetches).finally(() => {
            setIsLoading(false);
        });
    }, [month, year, dspEstimatesEnabled, dispatch, activeArtistProfileId]);

    const InputField = useCallback(({ label, value }) => (
        <div className="bg-white border-2 border-theme_clr1 font-manrope flex items-center justify-between gap-3 rounded-xl px-2 py-2">
            <div className="flex items-center gap-2 flex-1">
                <label className="text-theme_clr1 text-sm font-bold">{label}:</label>
                <span>
                    <input
                        type="text"
                        disabled
                        value={value}
                        className="bg-theme_clr2 text-white min-h-[22px] font-semibold rounded-3xl text-center text-xs px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-theme_clr2 w-28"
                    />
                </span>
            </div>
        </div>
    ), []);

    const highestRoiCategory =
        categoryROI?.highestROICategory == null
            ? "Streaming"
            : categoryROI?.highestROICategory?.category;

    const handleUnlockClick = needsLinkArtist ? onLinkArtist : onSubscribe;
    const unlockCtaLabel = needsLinkArtist ? "Link Your Artist" : "Subscribe to unlock";

    return (
        <div className="info-box h-full bg-white font-manrope flex flex-col justify-between gap-3 rounded-xl p-4 2xl:p-5">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
                <img src={DollorIcon} alt="Dollar Icon" className="w-6 h-6" />
                <MonthDropdown
                    getMonth={handleMonthChange}
                    getYear={handleYearChange}
                />
            </div>

            {isLoading ? (
                <div className="flex items-center justify-center py-8">
                    <div className="text-theme_clr1 text-sm">Loading...</div>
                </div>
            ) : (
                <>
                    <div className="flex flex-col gap-2">
                        <InputField
                            label="Logged Revenue"
                            value={`$${calculations.revenueForRoi.toFixed(2)}`}
                        />

                        <InputField
                            label="Logged Expenses"
                            value={`$${totalExpenses.toFixed(2)}`}
                        />

                        <div className="bg-white border-2 border-theme_clr1 font-inter flex flex-col gap-2 rounded-xl text-theme_clr1 px-3 py-2 sm:flex-row sm:items-center sm:justify-between">
                            <span className="font-semibold text-[14.5px] shrink-0">
                                Highest ROI: {dspEstimatesEnabled ? highestRoiCategory : ""}
                            </span>
                            {!dspEstimatesEnabled && (
                                <div className="flex flex-col items-end gap-1.5 text-right">
                                    <p className="text-xs leading-snug text-slate-600">
                                        Discover which revenue stream generates the highest return on your investment.
                                    </p>
                                    <button
                                        type="button"
                                        className="btn-custom3 inline-flex w-fit items-center justify-center gap-1.5 !px-3 !py-1.5 text-xs"
                                        onClick={handleUnlockClick}
                                    >
                                        {unlockCtaLabel}
                                        {!needsLinkArtist && (
                                            <img
                                                src={StarIcon}
                                                alt=""
                                                className="h-[14px] w-[14px] min-w-[14px]"
                                            />
                                        )}
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="cover">
                        <div className="text-theme_clr1 text-[12.26px] font-bold mb-[2px]">
                            {calculations.roi.toFixed(2)}%
                        </div>
                        <div className="text-theme_clr1 text-[12.8px]">ROI Calculator</div>
                    </div>
                </>
            )}

            <ToastContainer />
        </div>
    );
};
